interface SparseVoxelOctreeNode<T> {
  value?: T
  children?: Map<number, SparseVoxelOctreeNode<T>>
}

export default class SparseVoxelOctree<T> {
  constructor(maxDepth = 21) {
    this.maxDepth = maxDepth
  }

  maxDepth: number
  root: SparseVoxelOctreeNode<T> = {}

  clear() {
    this.root = {}
  }

  encodeCoordinate(value: number) {
    const integer = Math.trunc(value)
    return integer >= 0 ? integer * 2 : -integer * 2 - 1
  }

  childIndex(x: number, y: number, z: number, bit: number) {
    return (
      ((x >> bit) & 1) |
      (((y >> bit) & 1) << 1) |
      (((z >> bit) & 1) << 2)
    )
  }

  set(x: number, y: number, z: number, value: T) {
    const encodedX = this.encodeCoordinate(x)
    const encodedY = this.encodeCoordinate(y)
    const encodedZ = this.encodeCoordinate(z)
    let node = this.root

    for (let bit = this.maxDepth - 1; bit >= 0; bit--) {
      const index = this.childIndex(encodedX, encodedY, encodedZ, bit)
      if (!node.children) node.children = new Map()
      let child = node.children.get(index)
      if (!child) {
        child = {}
        node.children.set(index, child)
      }
      node = child
    }

    node.value = value
  }

  get(x: number, y: number, z: number): T | undefined {
    const encodedX = this.encodeCoordinate(x)
    const encodedY = this.encodeCoordinate(y)
    const encodedZ = this.encodeCoordinate(z)
    let node: SparseVoxelOctreeNode<T> | undefined = this.root

    for (let bit = this.maxDepth - 1; bit >= 0; bit--) {
      const index = this.childIndex(encodedX, encodedY, encodedZ, bit)
      node = node.children?.get(index)
      if (!node) return undefined
    }

    return node.value
  }

  delete(x: number, y: number, z: number) {
    const encodedX = this.encodeCoordinate(x)
    const encodedY = this.encodeCoordinate(y)
    const encodedZ = this.encodeCoordinate(z)
    const path: {
      index: number
      node: SparseVoxelOctreeNode<T>
    }[] = []
    let node: SparseVoxelOctreeNode<T> | undefined = this.root

    for (let bit = this.maxDepth - 1; bit >= 0; bit--) {
      const index = this.childIndex(encodedX, encodedY, encodedZ, bit)
      if (!node?.children) return
      const child = node.children.get(index)
      if (!child) return
      path.push({ index, node })
      node = child
    }

    if (!node) return
    delete node.value

    for (let i = path.length - 1; i >= 0; i--) {
      const entry = path[i]
      if (!entry) continue
      const { index, node: parent } = entry
      const child = parent.children?.get(index)
      if (
        child?.value !== undefined ||
        (child?.children && child.children.size > 0)
      ) {
        break
      }
      parent.children?.delete(index)
    }
  }

  forEach(callback: (value: T) => void) {
    const visit = (node: SparseVoxelOctreeNode<T>) => {
      if (node.value !== undefined) callback(node.value)
      if (!node.children) return
      for (const child of node.children.values()) {
        visit(child)
      }
    }

    visit(this.root)
  }

  forEachInBounds(
    minX: number,
    minY: number,
    minZ: number,
    maxX: number,
    maxY: number,
    maxZ: number,
    getPosition: (value: T) => { x: number; y: number; z: number },
    callback: (value: T) => void
  ) {
    this.forEach(value => {
      const position = getPosition(value)
      if (
        position.x >= minX &&
        position.x <= maxX &&
        position.y >= minY &&
        position.y <= maxY &&
        position.z >= minZ &&
        position.z <= maxZ
      ) {
        callback(value)
      }
    })
  }
}
